import { Fragment, useCallback, type FC, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';

import { NestedPageHeader } from '#packages/web/layouts/page/header';
import {
  PageContent,
  type PageContentProps,
} from '#packages/web/layouts/page/page-content';

import { PageErrorBoundary } from './page-error-boundary';

export type NestedPageLayoutProps = {
  /**
   * Title to display at the top of the page.
   */
  title: string;

  /**
   * Props to pass to the PageContent component.
   */
  pageContentProps?: Partial<PageContentProps>;

  /**
   * Callback when the error boundary action button is clicked.
   */
  onErrorActionClick?: () => void;

  /**
   * Optional header children to render inside the header.
   */
  headerChildren?: React.ReactNode;
};

export const NestedPageLayout: FC<PropsWithChildren<NestedPageLayoutProps>> = ({
  title,

  pageContentProps,
  onErrorActionClick = useCallback(() => window.location.reload(), []),

  headerChildren,

  children,
}) => {
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Fragment>
      <NestedPageHeader title={title} onNavigationIconClick={goBack}>
        {headerChildren}
      </NestedPageHeader>
      <PageContent {...pageContentProps}>
        <PageErrorBoundary onActionClick={onErrorActionClick}>
          {children}
        </PageErrorBoundary>
      </PageContent>
    </Fragment>
  );
};
